/**
 * Legacy Vault — true end-to-end encryption for sensitive media.
 *
 * The per-tree key is derived from a passphrase with PBKDF2 (SHA-256, 310k iters)
 * and NEVER leaves this device. The server stores only: the random salt, an
 * encrypted check value (to verify the passphrase), and ciphertext blobs.
 * Losing the passphrase means vault items are unrecoverable — by design.
 */

const CHECK_PLAINTEXT = 'family-ancestry-vault-v1'
const ITERATIONS = 310_000

// unlocked keys live in memory only, keyed by treeId
const keyCache = new Map<string, CryptoKey>()
// passphrases retained in memory ONLY while unlocked, to enable PIN-wrapped sharing
const passCache = new Map<string, string>()

function hexToBuf(hex: string): Uint8Array {
  return new Uint8Array(hex.match(/.{2}/g)!.map(b => parseInt(b, 16)))
}
function bufToHex(buf: ArrayBuffer | Uint8Array): string {
  return [...new Uint8Array(buf as ArrayBuffer)].map(b => b.toString(16).padStart(2, '0')).join('')
}

async function deriveKey(passphrase: string, saltHex: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: hexToBuf(saltHex) as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** Initialize a vault: returns {vault_salt, vault_check} to persist via tree PUT. Caches the key. */
export async function createVault(treeId: string, passphrase: string): Promise<{ vault_salt: string; vault_check: string }> {
  const salt = bufToHex(crypto.getRandomValues(new Uint8Array(16)))
  const key = await deriveKey(passphrase, salt)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, new TextEncoder().encode(CHECK_PLAINTEXT))
  keyCache.set(treeId, key)
  passCache.set(treeId, passphrase)
  return { vault_salt: salt, vault_check: `${bufToHex(iv)}:${bufToHex(ct)}` }
}

/** Unlock an existing vault. Returns false on wrong passphrase. Caches the key on success. */
export async function unlockVault(treeId: string, passphrase: string, vaultSalt: string, vaultCheck: string): Promise<boolean> {
  try {
    const key = await deriveKey(passphrase, vaultSalt)
    const [ivHex, ctHex] = vaultCheck.split(':')
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: hexToBuf(ivHex) as BufferSource }, key, hexToBuf(ctHex) as BufferSource)
    if (new TextDecoder().decode(pt) !== CHECK_PLAINTEXT) return false
    keyCache.set(treeId, key)
    passCache.set(treeId, passphrase)
    return true
  } catch { return false }
}

export function isUnlocked(treeId: string): boolean {
  return keyCache.has(treeId)
}

export function lockVault(treeId: string): void {
  keyCache.delete(treeId)
  passCache.delete(treeId)
}

// ── PIN-wrapped key sharing (server only ever sees ciphertext) ──

/** Wrap the unlocked vault passphrase under a one-time PIN for sharing. */
export async function wrapForShare(treeId: string, pin: string): Promise<{ ciphertext: string; iv: string; salt: string }> {
  const passphrase = passCache.get(treeId)
  if (!passphrase) throw new Error('Vault must be unlocked on this device to share it')
  const salt = bufToHex(crypto.getRandomValues(new Uint8Array(16)))
  const key = await deriveKey(pin, salt)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, new TextEncoder().encode(passphrase))
  return { ciphertext: bufToHex(ct), iv: bufToHex(iv), salt }
}

/** Unwrap a redeemed share with its PIN → the vault passphrase (or null if PIN wrong). */
export async function unwrapShare(pin: string, ciphertext: string, ivHex: string, saltHex: string): Promise<string | null> {
  try {
    const key = await deriveKey(pin, saltHex)
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: hexToBuf(ivHex) as BufferSource }, key, hexToBuf(ciphertext) as BufferSource)
    return new TextDecoder().decode(pt)
  } catch { return null }
}

/** Encrypt a file client-side before upload. Returns ciphertext blob + iv (hex) for the server row. */
export async function encryptFile(treeId: string, file: File): Promise<{ blob: Blob; ivHex: string }> {
  const key = keyCache.get(treeId)
  if (!key) throw new Error('Vault locked')
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, await file.arrayBuffer())
  return { blob: new Blob([ct], { type: 'application/octet-stream' }), ivHex: bufToHex(iv) }
}

/** Decrypt a downloaded ciphertext buffer back to a typed Blob for display. */
export async function decryptToBlob(treeId: string, cipher: ArrayBuffer, ivHex: string, mime: string): Promise<Blob> {
  const key = keyCache.get(treeId)
  if (!key) throw new Error('Vault locked')
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: hexToBuf(ivHex) as BufferSource }, key, cipher)
  return new Blob([pt], { type: mime })
}
